import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { TenantBrandingBridge } from "@/components/TenantBrandingBridge";
import { TenantHostBoot } from "@/components/TenantHostBoot";
import { FxRatesProvider } from "@/components/FxRatesProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DashboardShell } from "@/components/DashboardShell";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Checkout from "@/pages/Checkout";
import EmpireEnquiry from "@/pages/EmpireEnquiry";
import Upgrade from "@/pages/Upgrade";
import Dashboard from "@/pages/Dashboard";
import ComingSoon from "@/pages/ComingSoon";

import Clients from "@/pages/fitness/Clients";
import ClientDetail from "@/pages/fitness/ClientDetail";
import Workouts from "@/pages/fitness/Workouts";
import ExerciseLibrary from "@/pages/fitness/ExerciseLibrary";
import AIWorkoutGenerator from "@/pages/fitness/AIWorkoutGenerator";

import POS from "@/pages/cafe/POS";
import Menu from "@/pages/cafe/Menu";
import KitchenBoard from "@/pages/cafe/KitchenBoard";
import CoffeeInventory from "@/pages/cafe/CoffeeInventory";

import AIAssistantPage from "@/pages/ai/Assistant";
import GrowthOverview from "@/pages/growth/Overview";
import GrowthLeads from "@/pages/growth/Leads";
import GrowthLeadDetail from "@/pages/growth/LeadDetail";
import GrowthChat from "@/pages/growth/Chat";
import AutomationList from "@/pages/automation/AutomationList";
import AutomationBuilder from "@/pages/automation/AutomationBuilder";
import AutomationRuns from "@/pages/automation/AutomationRuns";
import PipelineHome from "@/pages/pipelines/PipelineHome";
import B2CIntelHome from "@/pages/pipelines/B2CIntelHome";
import Branding from "@/pages/settings/Branding";
import Team from "@/pages/settings/Team";
import ModulesPage from "@/pages/settings/Modules";
import CustomDomain from "@/pages/settings/Domain";
import WorkspaceOverview from "@/pages/settings/WorkspaceOverview";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import SalesCRM from "@/pages/admin/SalesCRM";
import WhatsAppLog from "@/pages/admin/WhatsAppLog";
import WhatsAppTemplates from "@/pages/admin/WhatsAppTemplates";
import UnsubscribesPage from "@/pages/admin/UnsubscribesPage";
import LaieOverview from "@/pages/laie/Overview";
import MarketingCRM from "@/pages/marketing/MarketingCRM";
import PublicForm from "@/pages/marketing/PublicForm";

import "@/App.css";

function SuperAdminRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user || user.role !== "super_admin") return <Navigate to="/app" replace />;
  return children;
}

function AppShellRoute({ children }) {
  return (
    <ProtectedRoute>
      <TenantBrandingBridge />
      <DashboardShell>{children}</DashboardShell>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            richColors
            closeButton
            // Container ignores pointer events (individual toasts still receive
            // them). Prevents the toast stack from intercepting clicks on
            // fixed/sticky headers underneath (e.g. Automation Builder Test/Save).
            toastOptions={{ style: { pointerEvents: "auto" } }}
            style={{ pointerEvents: "none" }}
          />
          <TenantHostBoot />
          <FxRatesProvider />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/empire-enquiry" element={<EmpireEnquiry />} />
            <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
            <Route path="/f/:tenantSlug/:formSlug" element={<PublicForm />} />

            <Route path="/app" element={<AppShellRoute><Dashboard /></AppShellRoute>} />

            <Route path="/app/fitness/clients" element={<AppShellRoute><Clients /></AppShellRoute>} />
            <Route path="/app/fitness/clients/:id" element={<AppShellRoute><ClientDetail /></AppShellRoute>} />
            <Route path="/app/fitness/workouts" element={<AppShellRoute><Workouts /></AppShellRoute>} />
            <Route path="/app/fitness/exercises" element={<AppShellRoute><ExerciseLibrary /></AppShellRoute>} />
            <Route path="/app/fitness/ai-workout" element={<AppShellRoute><AIWorkoutGenerator /></AppShellRoute>} />

            <Route path="/app/cafe/pos" element={<AppShellRoute><POS /></AppShellRoute>} />
            <Route path="/app/cafe/menu" element={<AppShellRoute><Menu /></AppShellRoute>} />
            <Route path="/app/cafe/coffee-inventory" element={<AppShellRoute><CoffeeInventory /></AppShellRoute>} />
            <Route path="/app/cafe/kitchen" element={<AppShellRoute><KitchenBoard /></AppShellRoute>} />

            <Route path="/app/ai" element={<AppShellRoute><AIAssistantPage /></AppShellRoute>} />

            <Route path="/app/settings/workspace" element={<AppShellRoute><WorkspaceOverview /></AppShellRoute>} />
            <Route path="/app/settings/branding" element={<AppShellRoute><Branding /></AppShellRoute>} />
            <Route path="/app/settings/domain" element={<AppShellRoute><CustomDomain /></AppShellRoute>} />
            <Route path="/app/settings/team" element={<AppShellRoute><Team /></AppShellRoute>} />
            <Route path="/app/settings/modules" element={<AppShellRoute><ModulesPage /></AppShellRoute>} />

            <Route path="/app/retail" element={<AppShellRoute><ComingSoon moduleName="Retail Store" eta="Q2 2026" /></AppShellRoute>} />
            <Route path="/app/retreats" element={<AppShellRoute><ComingSoon moduleName="Retreat Management" eta="Q3 2026" /></AppShellRoute>} />
            <Route path="/app/automation" element={<AppShellRoute><AutomationList /></AppShellRoute>} />
            <Route path="/app/automation/runs" element={<AppShellRoute><AutomationRuns /></AppShellRoute>} />
            <Route path="/app/automation/:id" element={<AppShellRoute><AutomationBuilder /></AppShellRoute>} />
            <Route path="/app/jaydiets" element={<AppShellRoute><PipelineHome pipeline="b2c_jaydiets" /></AppShellRoute>} />
            <Route path="/app/b2c/intel" element={<AppShellRoute><B2CIntelHome /></AppShellRoute>} />
            <Route path="/app/b2c/coffee" element={<AppShellRoute><PipelineHome pipeline="b2c_coffee" /></AppShellRoute>} />
            <Route path="/app/b2c/wellness" element={<AppShellRoute><PipelineHome pipeline="b2c_wellness" /></AppShellRoute>} />
            <Route path="/app/b2c/retreat" element={<AppShellRoute><PipelineHome pipeline="b2c_retreat" /></AppShellRoute>} />
            <Route path="/app/marketing" element={<AppShellRoute><MarketingCRM /></AppShellRoute>} />

            <Route path="/app/growth" element={<AppShellRoute><GrowthOverview /></AppShellRoute>} />
            <Route path="/app/growth/leads" element={<AppShellRoute><GrowthLeads /></AppShellRoute>} />
            <Route path="/app/growth/leads/:id" element={<AppShellRoute><GrowthLeadDetail /></AppShellRoute>} />
            <Route path="/app/growth/chat" element={<AppShellRoute><GrowthChat /></AppShellRoute>} />

            <Route path="/app/admin" element={<AppShellRoute><SuperAdminRoute><AdminDashboard /></SuperAdminRoute></AppShellRoute>} />
            <Route path="/app/admin/crm" element={<AppShellRoute><SuperAdminRoute><SalesCRM /></SuperAdminRoute></AppShellRoute>} />
            <Route path="/app/admin/whatsapp" element={<AppShellRoute><SuperAdminRoute><WhatsAppLog /></SuperAdminRoute></AppShellRoute>} />
            <Route path="/app/admin/whatsapp/templates" element={<AppShellRoute><SuperAdminRoute><WhatsAppTemplates /></SuperAdminRoute></AppShellRoute>} />
            <Route path="/app/admin/unsubscribes" element={<AppShellRoute><SuperAdminRoute><UnsubscribesPage /></SuperAdminRoute></AppShellRoute>} />
            <Route path="/app/laie" element={<AppShellRoute><SuperAdminRoute><LaieOverview /></SuperAdminRoute></AppShellRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
